import { gql } from '@apollo/client';

export const PARTNERS = gql`
  query Partners($params: PartnersRequestParams) {
    partners(params: $params) {
      results {
        id
        name
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const PARTNER = gql`
  query Partner($id: Int!) {
    partner(id: $id) {
      organizationId
      name
      emailThemeId
      internal
      paymentOfInvoiceAllowed
      payInvoiceByLink
      paymentTermsDays
      businessModel
      managedByUserId
      partnerType
      invoicingFrequency
      invoicingCurrencyCode
      mainLanguageIsoCode
      vatRegion
      journeyFreeCancellationWindowHours
      vatNumber
      discount
      ibanCode
      ibanAccountHolder
      swiftCode
      permittedFleets {
        id
        fleet {
          id
          name
          transferCompany {
            id
            organization {
              name
            }
          }
        }
      }
      bookingPreferences {
        overridePassengersToMaximumVehicleCapacity
        overrideLuggageToMaximumVehicleCapacity
      }
      vehicleLimitations {
        excludedVehicleCategories
      }
      invoiceAddress {
        countryCode
      }
      whiteLabel {
        id
        domainName
        fullDomainName
        allowTravellersEditingJourneys
        active
        settings {
          showHubFirst
        }
        tagManagers {
          tagManagerId
        }
        helpdeskUrl
        onlineBookingToolEnabled
      }
    }
    emailThemes {
      id
      name
      defaultTheme
    }
    currencies
  }
`;

export const UPDATE_PARTNER = gql`
  mutation UpdatePartner($id: Int!, $data: PartnerParams!) {
    updatePartner(id: $id, data: $data) {
      id
    }
  }
`;

export const CREATE_PARTNER = gql`
  mutation CreatePartner($data: PartnerParams!) {
    createPartner(data: $data) {
      id
      name
    }
  }
`;

export const PARTNER_ADDRESSES = gql`
  query PartnerPostalAddress($id: Int!) {
    partnerPostalAddress(id: $id) {
      companyName
      toTheAttentionOf
      street
      streetNumber
      streetNumberExtension
      city
      postOfficeBox
      zipCode
      countryCode
      email
    }
    partnerInvoiceAddress(id: $id) {
      companyName
      toTheAttentionOf
      street
      streetNumber
      streetNumberExtension
      city
      postOfficeBox
      zipCode
      countryCode
      email
    }
    countries {
      code
      displayName
    }
  }
`;

export const COMMUNICATION_PREFERENCES = gql`
  query CommunicationPreferences($id: Int!) {
    communicationPreferences(id: $id) {
      sendBookingPlacedEmail
      sendJourneyConfirmedEmail
      sendJourneyPlannedEmail
      receiveWebhookEvents
      sendJourneyCancellationEmails
      webhook
      sendPaymentLinks
      customerServiceEmail
      sendJourneyCancellationEmailsToTravellers
      sendSurveyEmailToTravellers
      sendPrePickupSMS
      sendBookingCreatedSMS
      sendPhoneNumberVerificationSMS
      sendDriverDetailsSMS
      sendAlertEmailsToTravellers
      emailSenderAddress
      emailSenderName
      webhookDeliveryAttemptsCount
      sendJourneyChangeEmailToBooker
      webhookHmac
    }
  }
`;

export const UPDATE_COMMUNICATION_PREFERENCES = gql`
  mutation UpdatCommunicationPreferences($data: CommunicationPreferencesInput!, $id: Int!) {
    updateCommunicationPreferences(data: $data, id: $id)
  }
`;

export const STYLE_CONFIGURATION = gql`
  query StyleConfiguration($id: Int!) {
    styleConfiguration(id: $id) {
      backgroundColor
      backgroundContrastColor,
      primaryColor
      primaryContrastColor
      secondaryColor
      secondaryContrastColor
      tertiaryColor
      tertiaryContrastColor
      heroImageUrl
      logoUrl
    }
  }
`;

export const UPDATE_STYLE_CONFIGURATION = gql`
  mutation UpdateStyleConfiguration($data: StyleConfigurationParams!, $id: Int!) {
    updateStyleConfiguration(data: $data, id: $id)
  }
`;

export const UPDATE_PARTNER_POSTAL_ADDRESS = gql`
  mutation UpdatePartnerPostalAddress($data: PartnerAddressInput!, $id: Int!) {
    updatePartnerPostalAddress(data: $data, id: $id)
  }
`;

export const UPDATE_PARTNER_INVOICE_ADDRESS = gql`
  mutation UpdatePartnerInvoiceAddress($data: PartnerAddressInput!, $id: Int!) {
    updatePartnerInvoiceAddress(data: $data, id: $id)
  }
`;

export const GLOBAL_PRICE_SETTINGS = gql`
  query GlobalPriceSettings($data: GlobalPriceSettingsParamsInput!) {
    globalPriceSettings(data: $data) {
      results {
        id
        partnerId
        type
        partnerCommission
        platformFee
        platformMarkup
        platformMarkupCurrencyCode
        priceIncludesDiscount
        journeyDirection
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const HUB_PRICE_SETTINGS = gql`
  query hubPriceSettings($data: HubPriceSettingsParamsInput!) {
    hubPriceSettings(data: $data) {
      results {
        id
        partnerId
        type
        partnerCommission
        platformFee
        platformMarkup
        platformMarkupCurrencyCode
        hubId
        hubName
        vehicleCategories
        hubIataCode
        priceIncludesDiscount
        journeyDirection
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const CREATE_HUB_PRICE_SETTING = gql`
  mutation CreateHubPriceSetting($data: HubPriceSettingInput!) {
    createHubPriceSetting(data: $data)
  }
`;

export const UPDATE_HUB_PRICE_SETTING = gql`
  mutation UpdateHubPriceSetting($data: HubPriceSettingInput!) {
    updateHubPriceSetting(data: $data)
  }
`;

export const REMOVE_PRICE_SETTING = gql`
  mutation removePriceSettings($id: Int!) {
    removePriceSettings(id: $id)
  }
`;

export const GEO_ZONE_PRICE_SETTINGS = gql`
  query GeoZonePriceSettings($data: GeoZonePriceSettingsParamsInput!) {
    geoZonePriceSettings(data: $data) {
      results {
        id
        partnerId
        type
        partnerCommission
        platformFee
        platformMarkup
        platformMarkupCurrencyCode
        hubId
        hubName
        geoZoneName
        vehicleCategories
        hubIataCode
        geoZoneId
        priceIncludesDiscount
        journeyDirection
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const FIXED_PRICE_SETTINGS = gql`
  query FixedPriceSettings($data: FixedPriceSettingsParamsInput!) {
    fixedPriceSettings(data: $data) {
      results {
        id
        partnerId
        type
        partnerCommission
        hubId
        hubName
        geoZoneName
        geoZoneId
        vehicleCategories
        fixedPriceInbound
        fixedPriceOutbound
        fixedPriceCurrencyCode
        fixedPriceIncludesMeetAndGreet
        priceIncludesDiscount
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const GEO_ZONES = gql`
  query PartnerGeoZones($data: PartnerGeoZonesParamsInput!) {
    partnerGeoZones(data: $data) {
      results {
        id
        name
        polygon {
          lng
          lat
        }
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const GEO_ZONE = gql`
  query PartnerGeoZone($geoZoneId: Int!) {
    partnerGeoZone(geoZoneId: $geoZoneId) {
      id
      name
      polygon {
        lng
        lat
      }
    }
  }
`;

export const GEO_ZONES_IN_RADIUS = gql`
  query PartnerGeoZonesInRadius($data: PartnerGeoZonesInRadiusRequestParams!) {
    partnerGeoZonesInRadius(data: $data) {
      id
      name
      polygon {
        lng
        lat
      }
    }
  }
`;

export const CREATE_GEO_ZONE_PRICE_SETTING = gql`
  mutation CreateGeoZonePriceSetting($data: CreateGeoZonePriceSettingInput!) {
    createGeoZonePriceSetting(data: $data)
  }
`;

export const UPDATE_GEO_ZONE_PRICE_SETTING = gql`
  mutation UpdateGeoZonePriceSetting($data: UpdateGeoZonePriceSettingInput!) {
    updateGeoZonePriceSetting(data: $data)
  }
`;

export const CREATE_FIXED_PRICE_SETTING = gql`
  mutation CreateFixedPriceSetting($data: FixedPriceSettingInput!) {
    createFixedPriceSetting(data: $data)
  }
`;

export const UPDATE_FIXED_PRICE_SETTING = gql`
  mutation UpdateFixedPriceSetting($data: FixedPriceSettingInput!) {
    updateFixedPriceSetting(data: $data)
  }
`;

export const CREATE_GLOBAL_PRICE_SETTING = gql`
mutation CreateGlobalPriceSetting($data: GlobalPriceSettingInput!) {
  createGlobalPriceSetting(data: $data)
}
`;

export const UPDATE_GLOBAL_PRICE_SETTING = gql`
  mutation UpdateGlobalPriceSetting($data: GlobalPriceSettingInput!) {
    updateGlobalPriceSetting(data: $data)
  }
`;

export const REMOVE_GEO_ZONE = gql`
  mutation RemovePartnerGeoZone($id: Int!) {
    removePartnerGeoZone(id: $id)
  }
`;

export const CREATE_GEO_ZONE = gql`
  mutation CreatePartnerGeoZone($data: PartnerGeoZoneParams!) {
    createPartnerGeoZone(data: $data)
  }
`;

export const UPDATE_GEO_ZONE = gql`
  mutation UpdatePartnerGeoZone($data: PartnerGeoZoneParams!) {
    updatePartnerGeoZone(data: $data)
  }
`;

export const COPY_PRICE_SETTINGS = gql`
  mutation CopyPriceSettings($params: PriceSettingsParams!) {
    copyPriceSettings(params: $params)
  }
`;

export const CREATE_SERVICE_ACCOUNT = gql`
  mutation CreateServiceAccount($data: ServiceAccountParams!, $id: Int!) {
    createServiceAccount(data: $data, id: $id){
      userId
      password
      existingUser
    }
  }
`;

export const UPDATE_WHITE_LABEL = gql`
  mutation UpdateWhiteLabel($params: WhiteLabelParams!) {
    updateWhiteLabel(params: $params)
  }
`;

export const CREATE_WHITE_LABEL = gql`
  mutation CreateWhiteLabel($params: WhiteLabelParams!) {
    createWhiteLabel(params: $params)
  }
`;

export const ACTIVATE_WHITE_LABEL = gql`
  mutation ActivateWhiteLabel($id: Int!) {
    activateWhiteLabel(id: $id)
  }
`;

export const DEACTIVATE_WHITE_LABEL = gql`
  mutation DeactivateWhiteLabel($id: Int!) {
    deactivateWhiteLabel(id: $id)
  }
`;

export const CREATE_STYLING_SETTING = gql`
  mutation CreateStylingSetting($params: StylingSettingParams!) {
    createStylingSetting(params: $params)
  }
`;

export const UPDATE_STYLING_SETTING = gql`
  mutation UpdateStylingSetting($params: StylingSettingParams!) {
    updateStylingSetting(params: $params)
  }
`;

export const REMOVE_STYLING_SETTING = gql`
  mutation RemoveStylingSetting($id: Int!) {
    removeStylingSetting(id: $id)
  }
`;

export const STYLING_SETTINGS = gql`
  query StylingSettings($params: StylingSettingsRequestParams!) {
    stylingSettings(params: $params) {
      results {
        id
        partnerId
        code
        type
        value
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const CREATE_APPLICATION_SETTING = gql`
  mutation CreateApplicationSetting($params: ApplicationSettingParams!) {
    createApplicationSetting(params: $params)
  }
`;

export const UPDATE_APPLICATION_SETTING = gql`
  mutation UpdateApplicationSetting($params: ApplicationSettingParams!) {
    updateApplicationSetting(params: $params)
  }
`;

export const REMOVE_APPLICATION_SETTING = gql`
  mutation RemoveApplicationSetting($id: Int!) {
    removeApplicationSetting(id: $id)
  }
`;

export const APPLICATION_SETTINGS = gql`
  query ApplicationSettings($params: ApplicationSettingsRequestParams!) {
    applicationSettings(params: $params) {
      results {
        id
        partnerId
        code
        type
        value
        category
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const TRANSFER_COMPANY_PREFERENCES = gql`
  query TransferCompanyPreferences($params: PreferedTransferCompanyRequestParams!) {
    transferCompanyPreferences(params: $params) {
      results {
        id
        partnerId
        type
        hubs {
          id
          name
          active
        }
        comment
        transferCompanyId
        transferCompanyName
        userId
        userName
        created
        updated
        deleted
      }
      pagination {
        currentPage
        pageSize
        totalPages
        totalResults
      }
    }
  }
`;

export const CREATE_TRANSFER_COMPANY_PREFERENCES = gql`
  mutation CreateTransferCompanyPreferences($params: TransferCompanyPreferencesParams!) {
    createTransferCompanyPreferences(params: $params)
  }
`;

export const UPDATE_TRANSFER_COMPANY_PREFERENCES = gql`
  mutation UpdateTransferCompanyPreferences($params: TransferCompanyPreferencesParams!) {
    updateTransferCompanyPreferences(params: $params)
  }
`;

export const REMOVE_TRANSFER_COMPANY_PREFERENCES = gql`
  mutation RemoveTransferCompanyPreferences($id: Int!) {
    removeTransferCompanyPreferences(id: $id)
  }
`;
